import { QueryKeys } from "@/constants/Query";
import axiosInstance from "@/lib/axios";
import { useInfiniteQuery } from "@tanstack/react-query";

const fetchChatMessages = async (
  roomId: string,
  cursor: string,
  signal: AbortSignal,
) => {
  let cursorParam = "";
  if (cursor) cursorParam = `?cursor=${cursor}`;
  return axiosInstance.get(`chat/rooms/${roomId}/messages${cursorParam}`, {
    signal,
  });
};

const getNextPageChatMessages = (response: { data: { cursor: string } }) =>
  response?.data?.cursor;

/**
 * Hook for fetching Universal Clinic chat messages with infinite scrolling/pagination
 */
export const useFetchMessages = (roomId: string) => {
  return useInfiniteQuery({
    queryFn: ({ pageParam: cursor, signal }) =>
      fetchChatMessages(roomId, cursor || "", signal),
    queryKey: QueryKeys.Patient.Chat.Messages.UNIVERSAL_CLINIC(roomId),
    enabled: Boolean(roomId),
    getNextPageParam: getNextPageChatMessages,
    initialPageParam: undefined,
    select: (data) => {
      // Keep messages in newest-first order for flipped container display
      const allMessages = data.pages.flatMap((page) => page.data.messages);

      // Collect all unique participants across all pages
      const participantsMap = new Map();
      for (const page of data.pages) {
        if (page.data.participants) {
          for (const participant of page.data.participants) {
            // Use id+type as key to deduplicate (id can be null for system)
            const key = `${participant.id}_${participant.type}`;
            if (!participantsMap.has(key)) {
              participantsMap.set(key, participant);
            }
          }
        }
      }

      return {
        pages: data.pages,
        messages: allMessages,
        room: data.pages[0]?.data?.room,
        participants: Array.from(participantsMap.values()),
      };
    },
    staleTime: 0, // Always consider data stale to force refetch on mount
    refetchOnMount: "always", // Always refetch when component mounts
  });
};
